import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function captureElement(el: HTMLElement) {
  return html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  } as Parameters<typeof html2canvas>[1]);
}

export async function downloadReportAsJpeg(
  el: HTMLElement,
  filename: string
): Promise<void> {
  const canvas = await captureElement(el);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
  );
  if (!blob) throw new Error("Could not create JPEG");
  triggerDownload(blob, filename.endsWith(".jpg") ? filename : `${filename}.jpg`);
}

export async function downloadReportAsPdf(
  el: HTMLElement,
  filename: string
): Promise<void> {
  const canvas = await captureElement(el);
  const imgData = canvas.toDataURL("image/jpeg", 0.92);
  const pxWidth = canvas.width;
  const pxHeight = canvas.height;

  // Prefer landscape for wide weekly/monthly grids
  const landscape = pxWidth > pxHeight;
  const pdf = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;
  const maxW = pageWidth - margin * 2;
  const maxH = pageHeight - margin * 2;

  const scale = Math.min(maxW / pxWidth, maxH / pxHeight);
  const drawW = pxWidth * scale;
  const drawH = pxHeight * scale;

  // Multi-page if taller than one page after fitting width
  if (drawH <= maxH) {
    pdf.addImage(imgData, "JPEG", margin, margin, drawW, drawH);
  } else {
    const widthScale = maxW / pxWidth;
    const sliceHeightPx = maxH / widthScale;
    let y = 0;
    let page = 0;
    while (y < pxHeight) {
      if (page > 0) pdf.addPage();
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = pxWidth;
      sliceCanvas.height = Math.min(sliceHeightPx, pxHeight - y);
      const ctx = sliceCanvas.getContext("2d");
      if (!ctx) break;
      ctx.drawImage(
        canvas,
        0,
        y,
        pxWidth,
        sliceCanvas.height,
        0,
        0,
        pxWidth,
        sliceCanvas.height
      );
      const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
      const sliceDrawH = sliceCanvas.height * widthScale;
      pdf.addImage(sliceData, "JPEG", margin, margin, maxW, sliceDrawH);
      y += sliceHeightPx;
      page += 1;
    }
  }

  pdf.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}
